import styled, { keyframes, css } from 'styled-components';

// ============================================================
// Modal CadastroCliente — design Clientes.html (hairline shell)
// ============================================================
// Visual: scrim escuro + blur, card centralizado 880px,
// form-grid 2-col com fields .full ocupando linha inteira.
// CSS vars (--cco-*) consumidas do global-style; reagem a
// theme.title (light/dark) automaticamente.

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Scrim — overlay full-screen com backdrop-blur, centraliza
// o modal via grid. z-index 50 fica abaixo de toasts (que usam
// z-index maior) mas acima de qualquer chrome da pagina.
export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 24px;

  @media (max-width: 720px) {
    padding: 0;
  }
`;

// Card do modal — 880px max, max-height pra nao vazar viewport
// em telas baixas (modal body rola internamente).
export const Modal = styled.div`
  width: min(880px, 100%);
  max-height: calc(100vh - 48px);
  background: var(--cco-surface);
  border: 1px solid var(--cco-line-strong);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeIn} 0.18s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);

  @media (max-width: 720px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

// Header — titulo + descricao + req-hint + close X
export const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 24px var(--cco-pad) 14px;
  border-bottom: 1px solid var(--cco-line);

  @media (max-width: 480px) {
    padding: 12px var(--cco-pad);
  }
`;

export const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--cco-ink);
`;

export const Desc = styled.div`
  font-size: 13.5px;
  color: var(--cco-ink-mute);
  margin-top: 4px;
`;

export const ReqHint = styled.div`
  font-size: 11.5px;
  color: var(--cco-ink-faint);
  margin-top: 8px;
  letter-spacing: 0.04em;

  .req {
    color: var(--cco-red);
    margin-right: 3px;
  }
`;

// Botao X — 34x34, hover red-soft
export const Close = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--cco-soft-surface);
  border: 1px solid var(--cco-line);
  display: grid;
  place-items: center;
  color: var(--cco-ink-mute);
  cursor: pointer;
  transition: all 0.15s;
  flex: none;
  font-size: 18px;
  line-height: 1;
  padding: 0;

  &:hover {
    background: rgba(244, 68, 92, 0.10);
    color: var(--cco-red);
    border-color: rgba(244, 68, 92, 0.4);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Body — area scrollavel central
export const ModalBody = styled.div`
  padding: 20px var(--cco-pad);
  overflow-y: auto;
  flex: 1;
`;

// Form-grid 2-col — em < 700px vira 1-col. Fields com prop
// $full ocupam a linha inteira (grid-column 1/-1).
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px 20px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  ${(props) =>
    props.$full &&
    css`
      grid-column: 1 / -1;
    `}
`;

export const Label = styled.label`
  font-size: 13.5px;
  font-weight: 600;
  color: var(--cco-ink);

  .req {
    color: var(--cco-red);
    margin-left: 2px;
  }
`;

// Estilo base do input — usado pelo <input> nativo e tambem
// pelo wrapper do InputMask via className="inputMasks" abaixo.
// Border-radius 999px segue o design do handoff (pill).
const inputStyles = css`
  appearance: none;
  -webkit-appearance: none;
  height: 44px;
  width: 100%;
  padding: 0 16px;
  background: var(--cco-soft-surface-2);
  border: 1px solid var(--cco-line);
  border-radius: 999px;
  color: var(--cco-ink);
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  box-sizing: border-box;

  &::placeholder {
    color: var(--cco-ink-faint);
  }

  &:focus {
    border-color: var(--cco-cyan);
    background: rgba(28, 176, 216, 0.04);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  ${(props) =>
    props.$error &&
    css`
      border-color: var(--cco-red);
    `}
`;

export const Input = styled.input`
  ${inputStyles}
`;

// Wrapper pra InputMask — react-input-mask cria um <input>
// interno; precisamos estilizar via class na div externa.
export const MaskWrap = styled.div`
  .inputMasks {
    ${inputStyles}
    ${(props) =>
      props.$error &&
      css`
        border-color: var(--cco-red);
      `}
  }
`;

export const Select = styled.select`
  ${inputStyles}
  padding-right: 42px;
  cursor: pointer;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='rgba(255,255,255,.5)' d='M0 0h12L6 8z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 16px center;

  option {
    background: var(--cco-bg);
    color: var(--cco-ink);
  }
`;

export const ErrorText = styled.span`
  font-size: 12px;
  color: var(--cco-red);
  margin-top: -2px;
  line-height: 1.3;
`;

// Footer — divisor + mensagem de confirmacao + botoes
export const ModalFoot = styled.div`
  padding: 18px var(--cco-pad) 24px;
  border-top: 1px solid var(--cco-line);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    padding: 12px var(--cco-pad);
  }
`;

export const Confirm = styled.div`
  color: var(--cco-ink-mute);
  font-size: 13px;
`;

export const SaveBtn = styled.button`
  height: 48px;
  padding: 0 64px;
  min-width: 300px;
  background: var(--cco-cyan);
  color: #06151a;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, filter 0.15s;

  &:hover:not(:disabled) {
    background: #3ec0e0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 700px) {
    min-width: 0;
    width: 100%;
    padding: 0 24px;
  }
`;

// Tela de sucesso (telaConcluido) — icon centralizado + mensagem
// + botao CONCLUIR. Substitui body+foot quando telaConcluido === true.
export const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 48px var(--cco-pad);
  text-align: center;
`;

export const SuccessIcon = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

export const SuccessText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--cco-ink);
  letter-spacing: -0.01em;
`;
